import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Download, Check, Eye, FileSignature, FileText } from 'lucide-react';
import { toast } from 'react-toastify';
import { useClientDocs } from '../hooks/useClientDocs';
import { recordDocumentOpen } from '../functions/portalDocsApi';
import {
  Card,
  StatCard,
  StatusBadge,
  Th,
  theadClass,
  IconButton,
  searchInputClass,
  PageHeader,
  EmptyState,
  Spinner,
} from './portal/PortalUI';

const ClientPortalDashboard = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const {
    myDocuments,
    isLoadingMyDocuments,
    isErrorMyDocuments,
    myDocumentsError,
    downloadDocument,
    isDownloading,
    getShareableLink,
    isFetchingShareableLink,
  } = useClientDocs();

  const documents = myDocuments || [];

  const stats = useMemo(
    () => ({
      all: documents.length,
      pending: documents.filter((d) => d.status === 'pending_signature').length,
      signed: documents.filter((d) => d.status === 'signed').length,
    }),
    [documents]
  );

  const filteredDocuments = useMemo(() => {
    return documents.filter((doc) => {
      if (activeTab === 'pending' && doc.status !== 'pending_signature') return false;
      if (activeTab === 'signed' && doc.status !== 'signed') return false;
      if (searchTerm && !(doc.title || '').toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [documents, activeTab, searchTerm]);

  const statusLabel = (status) =>
    status === 'pending_signature'
      ? 'Pending Signature'
      : status === 'signed'
        ? 'Signed'
        : status === 'shared'
          ? 'Shared'
          : 'Draft';

  const handleView = async (doc) => {
    // Open the tab synchronously, inside the click's gesture chain, so iOS
    // Safari and other mobile popup blockers don't kill it. The real URL is
    // set once the shareable link resolves; on failure the tab is closed.
    const viewer = window.open('', '_blank');
    try {
      const data = await getShareableLink(doc._id);
      if (data && data.share_url) {
        // Fire-and-forget: attributes the view to this client without delaying
        // the navigation above.
        recordDocumentOpen(doc._id).catch((error) => {
          console.error('Error recording document open:', error);
        });
        if (viewer) viewer.location = data.share_url;
      } else {
        toast.error('Shareable link not available');
        if (viewer) viewer.close();
      }
    } catch (error) {
      console.error('Error fetching shareable link:', error);
      if (viewer) viewer.close();
    }
  };

  const handleDownload = async (doc) => {
    try {
      await downloadDocument(doc._id);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  if (isLoadingMyDocuments) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Spinner label="Loading your documents…" />
      </div>
    );
  }

  if (isErrorMyDocuments) {
    return (
      <div className="text-center py-12 text-red-600 dark:text-red-400">
        Error loading your documents: {myDocumentsError?.message}
      </div>
    );
  }

  return (
    <div>
      {/* Page header + search */}
      <PageHeader
        title="Documents"
        subtitle="Review, sign and download everything we share with you"
      >
        <div className="relative w-full sm:w-72">
          <input
            type="text"
            placeholder="Search documents"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={searchInputClass}
          />
          <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-grayteck-400" />
        </div>
      </PageHeader>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 mb-8">
        {[
          { id: 'all', title: 'All Documents', count: stats.all, icon: FileText },
          { id: 'pending', title: 'Pending Signature', count: stats.pending, icon: Eye },
          { id: 'signed', title: 'Signed', count: stats.signed, icon: Check },
        ].map((stat, i) => (
          <StatCard
            key={stat.id}
            title={stat.title}
            count={stat.count}
            icon={stat.icon}
            active={activeTab === stat.id}
            onClick={() => setActiveTab(stat.id)}
            delay={i * 0.05}
          />
        ))}
      </div>

      {/* Documents Table */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            {filteredDocuments.length === 0 ? (
              <EmptyState icon={FileSignature} title="No documents found">
                {searchTerm
                  ? 'Nothing matches your search. Try a different term.'
                  : 'Documents we share with you will appear here.'}
              </EmptyState>
            ) : (
              <table className="min-w-full">
                <thead>
                  <tr className={theadClass}>
                    <Th>Title</Th>
                    <Th>Created</Th>
                    <Th>Signed</Th>
                    <Th>Status</Th>
                    <Th align="right">Actions</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-grayteck-200 dark:divide-darkbg-600">
                  {filteredDocuments.map((doc, index) => (
                    <motion.tr
                      key={doc._id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.04 }}
                      className="hover:bg-grayteck-50 dark:hover:bg-darkbg-700 transition-colors duration-150"
                    >
                      <td className="px-4 sm:px-6 py-4 text-sm font-semibold text-grayteck-900 dark:text-grayteck-100">
                        <div className="flex items-center">
                          <span className="shrink-0 mr-3 p-1.5 rounded-md bg-grayteck-100 dark:bg-darkbg-700">
                            <FileSignature className="w-4 h-4 text-grayteck-600 dark:text-grayteck-300" />
                          </span>
                          <span className="truncate max-w-md">{doc.title}</span>
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-grayteck-700 dark:text-grayteck-400">
                        {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-grayteck-700 dark:text-grayteck-400">
                        {doc.signed_at ? new Date(doc.signed_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={doc.status} label={statusLabel(doc.status)} />
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right">
                        <div className="inline-flex items-center gap-1">
                          <IconButton
                            title="Download"
                            onClick={() => handleDownload(doc)}
                            disabled={isDownloading}
                          >
                            <Download className="w-[18px] h-[18px]" />
                          </IconButton>
                          <IconButton
                            title="View"
                            onClick={() => handleView(doc)}
                            disabled={isFetchingShareableLink}
                          >
                            <Eye className="w-[18px] h-[18px]" />
                          </IconButton>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      </motion.div>
    </div>
  );
};

export default ClientPortalDashboard;
